import {
  Box,
  Float,
  IconButton,
  InputProps,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaTrashAlt } from 'react-icons/fa'
import { LuUpload } from 'react-icons/lu'
import { toast } from 'react-toastify'

import { Avatar, AvatarProps } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import {
  FileUploadDropzone,
  FileUploadRoot,
  FileUploadRootProps,
  FileUploadTrigger,
} from '@/components/ui/file-upload'

import { PimpImageModal } from '../../molecules/Form/AccountManagement/User/PimpImageModal'

type ImageDropzoneFormProps = {
  label?: string
  description?: string
  register?: UseFormRegisterReturn
  avatarProps?: AvatarProps
  inputProps?: InputProps
  onFileChange: (
    file: File,
    closeModal: () => void,
    metadata?: { legend?: string; alt?: string }
  ) => void
  onDelete?: () => void
  preview?: string | false
  legend?: string
  alt?: string
  maxFiles?: number
  aspectRatio?: number | 'free' // 1 for square/avatar, 16/9 for material, 'free' for free
  onImageModalOpenChange?: (isOpen: boolean) => void
  /**
   * Skip {@link PimpImageModal}; chosen file is passed as-is to `onFileChange`
   * (avoids z-index issues inside a parent dialog).
   */
  directFileUpload?: boolean
  /** Shorter full-width dropzone (banner / preview style). */
  compactDropzone?: boolean
  /**
   * Presentation variant. `logoCard` renders a dashed box with the current
   * image preview, a "replace" trigger button and a hint line (square logo
   * pickers). Defaults to the standard dropzone/avatar behaviour.
   */
  variant?: 'default' | 'logoCard'
  /** Fallback name used to derive avatar initials in `logoCard` variant. */
  name?: string
  /** Label of the file-picker button in `logoCard` variant. */
  triggerLabel?: React.ReactNode
  /** Hint line shown under the picker in `logoCard` variant (e.g. formats). */
  hint?: React.ReactNode
} & Omit<FileUploadRootProps, 'onFileChange'>

type FileUploadChangeDetails = Parameters<
  NonNullable<FileUploadRootProps['onFileChange']>
>[0]

export const ImageDropzoneForm = ({
  label,
  description,
  avatarProps,
  onFileChange,
  onDelete,
  preview = false,
  legend,
  alt,
  maxFiles,
  aspectRatio = 16 / 9, // Default rectangular for material
  onImageModalOpenChange,
  directFileUpload = false,
  compactDropzone = false,
  variant = 'default',
  name,
  triggerLabel,
  hint,
  ...fileUploadProps
}: ImageDropzoneFormProps) => {
  const { open, onOpen, onClose: closeDisclosure } = useDisclosure()
  const { t } = useTranslation()
  const directInputRef = useRef<HTMLInputElement | null>(null)
  const [currentFile, setCurrentFile] = useState<File | string | null>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploadKey, setUploadKey] = useState(0)

  const isMulti = maxFiles && maxFiles > 1

  /**
   * Clearing `currentFile` unmounts {@link PimpImageModal} immediately. If that happens in the same
   * synchronous turn as closing the dialog, Zag may not tear down modal effects (scroll lock,
   * inert/aria-hidden on siblings, focus trap) and the page stays non-interactive. Defer clearing
   * until after `open` has become false and the dialog has committed closed.
   */
  useEffect(() => {
    if (directFileUpload) {
      return
    }
    if (!open && currentFile !== null) {
      const id = window.setTimeout(() => {
        setCurrentFile(null)
      }, 0)
      return () => window.clearTimeout(id)
    }
  }, [open, currentFile, directFileUpload])

  useEffect(() => {
    if (directFileUpload) {
      return
    }
    if (!currentFile && pendingFiles.length > 0) {
      setCurrentFile(pendingFiles[0])
      setPendingFiles((prev) => prev.slice(1))
      onOpen()
      onImageModalOpenChange?.(true)
    }
  }, [
    currentFile,
    directFileUpload,
    pendingFiles,
    onOpen,
    onImageModalOpenChange,
  ])

  useEffect(() => {
    onImageModalOpenChange?.(open)
  }, [open, onImageModalOpenChange])

  const handleFilesChange = (files: File[]) => {
    if (!files?.length) {
      return
    }
    if (directFileUpload) {
      const list = isMulti ? files : files.slice(0, 1)
      for (const file of list) {
        onFileChange(file, () => {})
      }
      return
    }
    setPendingFiles((prev) => [...prev, ...files])
  }

  const avatarBg = useColorModeValue('gray.800', 'gray.200')
  const avatarColor = useColorModeValue('white', 'gray.800')
  const dropzoneBg = useColorModeValue('gray.50', 'gray.800')

  const onRootFileChange = (details: FileUploadChangeDetails) => {
    if (details.rejectedFiles && details.rejectedFiles.length > 0) {
      toast.error(t('toasts:global.form.file.rejected'))
    }
    if (details.acceptedFiles && details.acceptedFiles.length > 0) {
      handleFilesChange(details.acceptedFiles)
    } else if (onDelete) {
      onDelete()
    }
    setUploadKey((prev) => prev + 1)
  }

  const modal =
    !directFileUpload && currentFile ? (
      <PimpImageModal
        isOpen={open}
        onClose={closeDisclosure}
        rawFile={currentFile instanceof File ? currentFile : undefined}
        preview={typeof currentFile === 'string' ? currentFile : undefined}
        legend={legend}
        alt={alt}
        onUpload={(file, closeModal, metadata) => {
          onFileChange(file, closeModal, metadata)
        }}
        aspectRatio={aspectRatio}
      />
    ) : null

  if (variant === 'logoCard') {
    return (
      <>
        <FileUploadRoot
          key={uploadKey}
          accept={['image/*']}
          maxFiles={1}
          onFileChange={onRootFileChange}
          {...fileUploadProps}
        >
          <VStack
            gap={3}
            p={5}
            w="full"
            borderWidth="2px"
            borderStyle="dashed"
            borderColor="border"
            rounded="2xl"
            bg={dropzoneBg}
            align="center"
          >
            <Box pos="relative">
              <Avatar
                size="xl"
                shape="rounded"
                src={typeof preview === 'string' ? preview : undefined}
                name={name}
                colorPalette="brand"
                {...avatarProps}
              />
              {onDelete && preview ? (
                <Float placement="top-end">
                  <IconButton
                    size="2xs"
                    rounded="full"
                    colorPalette="red"
                    color="white"
                    aria-label="remove image"
                    onClick={(e: React.MouseEvent) => {
                      onDelete()
                      e.stopPropagation()
                    }}
                  >
                    <FaTrashAlt />
                  </IconButton>
                </Float>
              ) : null}
            </Box>
            <FileUploadTrigger asChild>
              <Button variant="outline" size="sm">
                <LuUpload />
                {triggerLabel ?? t('global:form.dropzoneImage.label')}
              </Button>
            </FileUploadTrigger>
            {hint ? (
              <Text fontSize="xs" color="fg.muted" textAlign="center">
                {hint}
              </Text>
            ) : null}
          </VStack>
        </FileUploadRoot>
        {modal}
      </>
    )
  }

  return (
    <>
      <Stack direction={['column', 'row']} gap={6}>
        {!isMulti && preview ? (
          <Box display="inline-block" pos="relative">
            {directFileUpload ? (
              <input
                ref={directInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const f = e.target.files?.[0]
                  if (f) {
                    onFileChange(f, () => {})
                  }
                  e.target.value = ''
                }}
              />
            ) : null}
            <Avatar
              size="2xl"
              cursor="pointer"
              src={typeof preview === 'string' ? preview : undefined}
              bg={avatarBg}
              color={avatarColor}
              onClick={() => {
                if (!preview) {
                  return
                }
                if (directFileUpload) {
                  directInputRef.current?.click()
                  return
                }
                setCurrentFile(preview)
                onOpen()
              }}
              shape="rounded"
              {...avatarProps}
            />
            {onDelete && (
              <Float placement="top-end">
                <IconButton
                  size="sm"
                  rounded="full"
                  colorPalette="red"
                  color="white"
                  aria-label="remove image"
                  onClick={(e: React.MouseEvent) => {
                    onDelete()
                    e.stopPropagation()
                  }}
                >
                  <FaTrashAlt />
                </IconButton>
              </Float>
            )}
          </Box>
        ) : (
          <FileUploadRoot
            key={uploadKey}
            accept={['image/*']}
            maxFiles={maxFiles}
            onFileChange={onRootFileChange}
            {...fileUploadProps}
          >
            <FileUploadDropzone
              label={label || t('global:form.dropzoneImage.label')}
              description={
                compactDropzone
                  ? undefined
                  : description || t('global:form.dropzoneImage.description')
              }
              compact={compactDropzone}
              bg={dropzoneBg}
              w="full"
            />
          </FileUploadRoot>
        )}
      </Stack>
      {modal}
    </>
  )
}
